import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock, Navigation } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { Reveal, ChapterLabel } from "@/components/Reveal";

const EASE = [0.22, 1, 0.36, 1];

export const directionsUrl = (address) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${address}, ON, Canada`)}`;

function PageHeader({ title, testid }) {
  return (
    <section
      className="relative bg-navy grain pt-40 pb-24 overflow-hidden"
      data-testid={`${testid}-header`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="text-[11px] tracking-[0.35em] uppercase font-bold text-brand-sky mb-6"
        >
          Drywall Local 675
        </motion.p>
        <h1 className="font-heading font-bold uppercase text-white text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-none">
          {title.split("\n").map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{
                  duration: 0.9,
                  delay: 0.3 + i * 0.12,
                  ease: EASE,
                }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
      </div>
    </section>
  );
}

export default function Contact() {
  const { content } = useSite();
  const c = content.contact;

  const blocks = [
    {
      icon: Mail,
      label: "Email",
      value: c.email,
      href: `mailto:${c.email}`,
      testid: "contact-email",
    },
    {
      icon: Phone,
      label: "Main number",
      value: c.phone,
      href: `tel:${c.phone.replace(/\D/g, "")}`,
      testid: "contact-phone",
    },
    {
      icon: MapPin,
      label: "Address",
      value: c.address,
      testid: "contact-address",
    },
    {
      icon: Clock,
      label: "Office hours",
      value: c.hours,
      testid: "contact-hours",
    },
  ];

  return (
    <main data-testid="contact-page">
      <PageHeader title={"Contact\nus"} testid="contact" />

      <section className="py-24 sm:py-28" data-testid="contact-block-section">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <ChapterLabel
              number="01"
              label="The office"
              testid="office-chapter"
            />
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
            {blocks.map((b, i) => {
              const Card = b.href ? "a" : "div";
              return (
                <Reveal key={b.label} delay={i * 0.07}>
                  <Card
                    href={b.href}
                    data-testid={b.testid}
                    className={`group flex items-start gap-6 bg-white p-10 h-full transition-colors duration-300 ${b.href || b.testid === "contact-address" ? "hover:bg-brand" : "cursor-default"}`}
                  >
                    <b.icon className="h-7 w-7 text-brand group-hover:text-white shrink-0 mt-1 transition-colors duration-300" />
                    <div className="min-w-0">
                      <p className="text-[11px] tracking-[0.25em] uppercase font-bold text-slate-500 group-hover:text-white/70 mb-2 transition-colors duration-300">
                        {b.label}
                      </p>
                      <p className="text-lg font-semibold text-slate-900 group-hover:text-white transition-colors duration-300 break-words">
                        {b.value}
                      </p>
                      {b.testid === "contact-address" && (
                        <a
                          href={directionsUrl(c.address)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="get-directions-button"
                          className="inline-flex items-center gap-2 mt-5 bg-navy group-hover:bg-white text-white group-hover:text-navy text-[11px] font-bold tracking-[0.2em] uppercase px-5 py-3 transition-colors duration-300"
                        >
                          <Navigation className="h-3.5 w-3.5" /> Get directions
                        </a>
                      )}
                    </div>
                  </Card>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section id="map" className="pb-24 sm:pb-28" data-testid="map-section">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border-2 border-brand pointer-events-none" />
              <iframe
                title="Map — 222 Rowntree Dairy Road, Woodbridge"
                data-testid="contact-map"
                src="https://www.openstreetmap.org/export/embed.html?bbox=-79.5620%2C43.7780%2C-79.5360%2C43.7930&layer=mapnik&marker=43.7856%2C-79.5491"
                className="relative w-full h-[380px] border-0"
                loading="lazy"
              />
            </div>
            <a
              href="https://www.google.com/maps/search/?api=1&query=222+Rowntree+Dairy+Rd%2C+Woodbridge%2C+ON+L4L+9T2%2C+Canada"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="map-larger-link"
              className="inline-flex items-center gap-2 mt-6 text-xs font-bold tracking-[0.2em] uppercase text-brand hover:text-brand-dark transition-colors duration-300"
            >
              Open in Google Maps
            </a>
          </Reveal>
        </div>
      </section>

      <section
        className="py-24 sm:py-32 bg-slate-50"
        data-testid="staff-section"
      >
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <ChapterLabel
              number="02"
              label="Staff directory"
              testid="staff-chapter"
            />
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {content.staff.map((person, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div
                  className="group bg-white border border-slate-200 hover:border-brand p-8 h-full transition-colors duration-300"
                  data-testid={`staff-card-${i}`}
                >
                  <p className="text-[11px] tracking-[0.25em] uppercase font-bold text-brand mb-2">
                    {person.role}
                  </p>
                  <h3 className="font-heading text-2xl uppercase text-slate-900 mb-6">
                    {person.name}
                  </h3>
                  <div className="flex flex-col gap-3">
                    <a
                      href={`tel:${person.phone.replace(/\D/g, "")}`}
                      data-testid={`staff-call-${i}`}
                      className="inline-flex items-center gap-3 text-sm font-semibold text-slate-700 hover:text-brand transition-colors duration-300"
                    >
                      <Phone className="h-4 w-4 text-brand" />
                      {person.phone}
                    </a>
                    <a
                      href={`mailto:${person.email}`}
                      data-testid={`staff-email-${i}`}
                      className="inline-flex items-center gap-3 text-sm font-semibold text-slate-700 hover:text-brand transition-colors duration-300 break-all"
                    >
                      <Mail className="h-4 w-4 text-brand shrink-0" />
                      {person.email}
                    </a>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
